import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, Circle, FileText, Radio, Users } from "lucide-react";
import api, { apiError, ruleReasons } from "../lib/api";
import { fmtDate, fmtDateTime } from "../lib/labels";
import { userName, useUsers } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { Chip, EmptyState, PageHeader, ToneChip, ReasonsList } from "../components/common";
import { ActionButtons } from "../components/ops";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const MEETING_STATUS = {
  borrador: { label: "Borrador", tone: "muted" },
  preparacion: { label: "En preparación", tone: "info" },
  convocada: { label: "Convocada", tone: "warning" },
  en_curso: { label: "En curso", tone: "ai" },
  celebrada: { label: "Celebrada", tone: "success" },
  cancelada: { label: "Cancelada", tone: "danger" },
};
const MEETING_TYPES = { proyecto: "Proyecto", obra: "Obra", comite_direccion: "Comité de dirección", coordinacion: "Coordinación técnica" };
const ITEM_STATUS = { pendiente: { label: "Pendiente", tone: "muted" }, tratado: { label: "Tratado", tone: "success" }, aplazado: { label: "Aplazado", tone: "warning" } };
const ATTENDANCE_LABELS = { presente: "Presente", ausente: "Ausente", justificado: "Ausencia justificada" };

function CreateMeetingDialog({ projects, users }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [series, setSeries] = useState(false);
  const [f, setF] = useState({ meeting_type: "proyecto" });
  const mut = useMutation({
    mutationFn: async () => (await api.post(series ? "/reuniones/series" : "/reuniones", f)).data,
    onSuccess: (d) => {
      toast.success(series ? `Serie creada: ${d.count} ocurrencias` : "Reunión creada");
      setOpen(false); setF({ meeting_type: "proyecto" });
      qc.invalidateQueries({ queryKey: ["meetings"] });
    },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="create-meeting-open">Nueva reunión</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-meeting-dialog">
          <DialogHeader><DialogTitle>{series ? "Nueva serie recurrente" : "Nueva reunión"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={series} onChange={(e) => setSeries(e.target.checked)} data-testid="create-meeting-series-toggle" />
              Serie recurrente (semanal/quincenal/mensual)
            </label>
            <div><Label>Título *</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="create-meeting-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipo</Label>
                <Select value={f.meeting_type} onValueChange={(v) => setF({ ...f, meeting_type: v })}>
                  <SelectTrigger data-testid="create-meeting-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(MEETING_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Proyecto</Label>
                <Select value={f.project_id || "none"} onValueChange={(v) => setF({ ...f, project_id: v === "none" ? null : v })}>
                  <SelectTrigger data-testid="create-meeting-project"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin proyecto (global)</SelectItem>
                    {(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fecha y hora *</Label><Input type="datetime-local" value={(f.date || "").slice(0, 16)} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="create-meeting-date" /></div>
              <div><Label>Lugar / medio *</Label><Input value={f.location || ""} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Caseta de obra / Teams" data-testid="create-meeting-location" /></div>
            </div>
            {series && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Frecuencia</Label>
                  <Select value={f.frequency || "semanal"} onValueChange={(v) => setF({ ...f, frequency: v })}>
                    <SelectTrigger data-testid="create-meeting-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="semanal">Semanal</SelectItem>
                      <SelectItem value="quincenal">Quincenal</SelectItem>
                      <SelectItem value="mensual">Mensual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Ocurrencias (2-24)</Label><Input type="number" min="2" max="24" value={f.count || 4} onChange={(e) => setF({ ...f, count: Number(e.target.value) })} data-testid="create-meeting-count" /></div>
              </div>
            )}
            <Button data-testid="create-meeting-submit" disabled={!f.title || !f.date || !f.location || mut.isPending}
              onClick={() => mut.mutate({ ...f, date: f.date ? new Date(f.date).toISOString() : null })}>
              {series ? "Crear serie" : "Crear reunión"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DetailDialog({ meeting: m, onClose, users }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: detail, refetch } = useQuery({
    queryKey: ["meeting", m.id],
    queryFn: async () => (await api.get(`/reuniones/${m.id}`)).data,
    initialData: m,
  });
  const { data: pack } = useQuery({
    queryKey: ["meeting-pack", m.id],
    queryFn: async () => (await api.get(`/reuniones/${m.id}/pack`)).data,
  });
  const [itemTitle, setItemTitle] = useState("");
  const [itemOwner, setItemOwner] = useState("");
  const [err, setErr] = useState(null);
  const d = detail || m;
  const editable = ["borrador", "preparacion"].includes(d.status);
  const live = d.status === "en_curso";
  const mut = useMutation({
    mutationFn: async (fn) => fn(),
    onSuccess: () => { setErr(null); setItemTitle(""); setItemOwner(""); refetch(); qc.invalidateQueries({ queryKey: ["meetings"] }); qc.invalidateQueries({ queryKey: ["meeting-pack", m.id] }); },
    onError: (e) => setErr(e),
  });
  const imAttendee = (d.attendees || []).find((a) => a.user_id === user?.id);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl" data-testid="meeting-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {d.title} <Chip map={MEETING_STATUS} value={d.status} testid="meeting-detail-status" />
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <p className="text-xs text-slate-500" data-testid="meeting-detail-meta">
            {MEETING_TYPES[d.meeting_type]} · {fmtDateTime(d.date)} · {d.location} · Organiza: {userName(users.map, d.organizer_id)}
            {d.series_id && ` · Serie recurrente (ocurrencia ${d.series_seq})`}
          </p>

          {/* Readiness check */}
          <div>
            <p className="mb-1 text-sm font-semibold text-slate-800">Readiness check</p>
            <div className="grid gap-1 sm:grid-cols-2" data-testid="readiness-list">
              {(d.readiness || []).map((c) => (
                <div key={c.check} className="flex items-center gap-2 text-xs" data-testid={`readiness-${c.check}`}>
                  {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Circle className={`h-3.5 w-3.5 ${c.blocking ? "text-red-500" : "text-slate-300"}`} />}
                  <span className={c.ok ? "text-slate-600" : c.blocking ? "font-medium text-red-700" : "text-slate-400"}>{c.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Agenda */}
          <div>
            <p className="mb-1 text-sm font-semibold text-slate-800">Agenda ({(d.agenda_items || []).length} puntos)</p>
            <div className="space-y-1.5" data-testid="agenda-list">
              {(d.agenda_items || []).map((i) => (
                <div key={i.id} className="rounded-md border border-slate-200 px-3 py-2" data-testid={`agenda-item-${i.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-400">{i.order}.</span>
                      <span className="text-sm text-slate-800">{i.title}</span>
                      {!live && <Chip map={ITEM_STATUS} value={i.status} testid={`agenda-status-${i.id}`} />}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>{userName(users.map, i.owner_id) || <span className="font-medium text-red-600">sin propietario</span>}</span>
                      <span>{i.duration_min} min</span>
                      {editable && <button className="text-red-500 hover:underline" data-testid={`agenda-remove-${i.id}`}
                        onClick={() => mut.mutate(() => api.delete(`/reuniones/${d.id}/agenda/${i.id}`))}>quitar</button>}
                      {live && (
                        <Select value={i.status} onValueChange={(v) => mut.mutate(() => api.patch(`/reuniones/${d.id}/agenda/${i.id}`, { status: v }))}>
                          <SelectTrigger className="h-7 w-32" data-testid={`agenda-live-status-${i.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{Object.entries(ITEM_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  {i.requires_docs && (
                    <p className="mt-1 text-xs text-slate-500" data-testid={`agenda-docs-${i.id}`}>
                      Docs previa: {(i.docs || []).length ? i.docs.join(" · ") : <span className="font-medium text-red-600">falta documentación</span>}
                    </p>
                  )}
                  {i.live_notes && <p className="mt-1 text-xs italic text-slate-500" data-testid={`agenda-notes-${i.id}`}>Notas en vivo: {i.live_notes}</p>}
                </div>
              ))}
            </div>
            {editable && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Input className="w-64" placeholder="Nuevo punto de agenda" value={itemTitle} onChange={(e) => setItemTitle(e.target.value)} data-testid="agenda-new-title" />
                <Select value={itemOwner} onValueChange={setItemOwner}>
                  <SelectTrigger className="w-48" data-testid="agenda-new-owner"><SelectValue placeholder="Propietario" /></SelectTrigger>
                  <SelectContent>{(users.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" data-testid="agenda-add-submit"
                  disabled={!itemTitle || mut.isPending}
                  onClick={() => mut.mutate(() => api.post(`/reuniones/${d.id}/agenda`, { title: itemTitle, owner_id: itemOwner || null }))}>
                  Añadir punto
                </Button>
              </div>
            )}
          </div>

          {/* Asistentes */}
          <div>
            <p className="mb-1 text-sm font-semibold text-slate-800">Asistentes ({(d.attendees || []).length}) · Quórum: {d.quorum?.present}/{d.quorum?.required} (umbral {Math.round((d.quorum?.threshold || 0.5) * 100)}%)
              {d.quorum?.quorum_met ? <ToneChip label="Quórum OK" tone="success" testid="quorum-ok" /> : <ToneChip label="Sin quórum" tone="warning" testid="quorum-ko" />}
            </p>
            <div className="space-y-1" data-testid="attendees-list">
              {(d.attendees || []).map((a) => (
                <div key={a.user_id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-100 px-3 py-1.5 text-sm" data-testid={`attendee-${a.user_id}`}>
                  <span>{userName(users.map, a.user_id)} {a.required ? <span className="text-xs text-slate-400">(requerido)</span> : <span className="text-xs text-slate-400">(opcional)</span>}</span>
                  <span className="flex items-center gap-2 text-xs">
                    <ToneChip label={`Invitación: ${a.invitation}`} tone={a.invitation === "aceptada" ? "success" : a.invitation === "rechazada" ? "danger" : "muted"} testid={`attendee-inv-${a.user_id}`} />
                    {a.attendance && !live && <ToneChip label={ATTENDANCE_LABELS[a.attendance] || a.attendance} tone={a.attendance === "presente" ? "success" : "warning"} testid={`attendee-att-${a.user_id}`} />}
                    {live && (
                      <Select value={a.attendance || ""} onValueChange={(v) => mut.mutate(() => api.post(`/reuniones/${d.id}/asistencia`, { user_id: a.user_id, attendance: v }))}>
                        <SelectTrigger className="h-7 w-36" data-testid={`attendance-select-${a.user_id}`}><SelectValue placeholder="Asistencia" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="presente">Presente</SelectItem>
                          <SelectItem value="ausente">Ausente</SelectItem>
                          <SelectItem value="justificado">Justificado</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {editable && <button className="text-red-500 hover:underline" data-testid={`attendee-remove-${a.user_id}`}
                      onClick={() => mut.mutate(() => api.delete(`/reuniones/${d.id}/asistentes/${a.user_id}`))}>quitar</button>}
                  </span>
                </div>
              ))}
            </div>
            {editable && (
              <div className="mt-2 flex items-center gap-2">
                <Select onValueChange={(v) => mut.mutate(() => api.post(`/reuniones/${d.id}/asistentes`, { user_id: v, required: true }))}>
                  <SelectTrigger className="w-56" data-testid="attendee-add"><SelectValue placeholder="Añadir asistente (requerido)" /></SelectTrigger>
                  <SelectContent>{(users.users || []).filter((u) => !(d.attendees || []).some((a) => a.user_id === u.id)).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Respuesta de invitación del usuario actual */}
          {imAttendee && d.status === "convocada" && imAttendee.invitation === "pendiente" && (
            <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2" data-testid="invitation-respond">
              <span className="text-sm text-blue-800">Tienes una invitación pendiente (Outlook mock):</span>
              <Button size="sm" variant="outline" data-testid="invitation-accept" onClick={() => mut.mutate(() => api.post(`/reuniones/${d.id}/responder`, { response: "aceptada" }))}>Aceptar</Button>
              <Button size="sm" variant="outline" data-testid="invitation-reject" onClick={() => mut.mutate(() => api.post(`/reuniones/${d.id}/responder`, { response: "rechazada" }))}>Rechazar</Button>
            </div>
          )}

          {/* Meeting Pack */}
          {pack && (
            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="meeting-pack">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800"><FileText className="mr-1 inline h-3.5 w-3.5" />Meeting Pack</p>
                <span className="text-xs text-slate-400">Generado {fmtDateTime(pack.generated_at)}</span>
              </div>
              <p className="mt-1 text-xs text-slate-600">
                {pack.project ? `Proyecto: ${pack.project.name} · ` : ""}Organiza: {pack.organizer} · {pack.agenda_items.length} puntos
                ({pack.agenda_items.reduce((s, i) => s + (i.duration_min || 0), 0)} min) · {pack.attendees.length} asistentes
                ({pack.attendees.filter((a) => a.required).length} requeridos)
              </p>
              <div className="mt-2 space-y-1" data-testid="pack-items">
                {pack.agenda_items.map((i) => (
                  <p key={i.id} className="text-xs text-slate-600" data-testid={`pack-item-${i.id}`}>
                    <span className="font-mono text-slate-400">{i.order}.</span> {i.title} — {i.owner_name || "sin propietario"} ({i.duration_min} min)
                    {i.requires_docs && (i.docs?.length ? ` · docs: ${i.docs.join(", ")}` : " · falta documentación")}
                  </p>
                ))}
              </div>
            </div>
          )}

          {err && (ruleReasons(err) ? <ReasonsList reasons={ruleReasons(err)} /> : <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="meeting-error">{apiError(err)}</p>)}

          {/* Acciones de estado */}
          <div className="flex items-center justify-between border-t border-slate-100 pt-3">
            <div className="flex items-center gap-3">
              <a href={`/reuniones/${d.id}/validacion`} className="text-xs font-medium text-blue-700 hover:underline" data-testid="open-validation">
                Transcript y validación IA →
              </a>
              <a href={`/reuniones/${d.id}/acta`} className="text-xs font-medium text-blue-700 hover:underline" data-testid="open-minute">
                Acta, aprobación y distribución →
              </a>
              <a href={`/reuniones/${d.id}/seguimiento`} className="text-xs font-medium text-blue-700 hover:underline" data-testid="open-seguimiento">
                Seguimiento y continuidad →
              </a>
            </div>
            <ActionButtons item={d} endpoint="/reuniones" invalidate={[["meetings"], ["meeting", m.id], ["meeting-pack", m.id]]} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Reuniones() {
  const [sp, setSp] = useSearchParams();
  const users = useUsers();
  const { user } = useAuth();
  const canWrite = user && !(user.roles?.length === 1 && user.roles[0] === "VIEWER");
  const [detailId, setDetailId] = useState(null);
  const statusFilter = sp.get("status") || "todas";
  const { data: meetings } = useQuery({
    queryKey: ["meetings", statusFilter],
    queryFn: async () => (await api.get("/reuniones", { params: statusFilter === "todas" ? {} : { status: statusFilter } })).data,
  });
  const { data: projects } = useQuery({ queryKey: ["ops-projects"], queryFn: async () => (await api.get("/proyectos")).data });
  const detail = (meetings || []).find((m) => m.id === detailId);
  return (
    <div className="space-y-4" data-testid="reuniones-page">
      <PageHeader title="Reuniones, actas y decisiones"
        subtitle="Preparación controlada: agenda con propietarios, documentación previa, Meeting Pack, invitaciones, asistencia y quórum. Nada se celebra sin quórum."
        actions={<div className="flex items-center gap-2">
          <a href="/reuniones/cuadro-de-mando" className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50" data-testid="open-meetings-dashboard">Cuadro de mando →</a>
          {canWrite ? <CreateMeetingDialog projects={projects} users={users} /> : null}
        </div>}
        testid="reuniones-title" />
      <div className="flex flex-wrap gap-1" data-testid="meeting-filters">
        {["todas", "borrador", "preparacion", "convocada", "en_curso", "celebrada", "cancelada"].map((s) => (
          <button key={s} onClick={() => setSp((p) => { p.set("status", s); return p; })}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${statusFilter === s ? "bg-blue-800 text-white" : "bg-white text-slate-500 ring-1 ring-slate-200 hover:text-slate-800"}`}
            data-testid={`filter-${s}`}>
            {s === "todas" ? "Todas" : MEETING_STATUS[s]?.label}
          </button>
        ))}
      </div>
      {!meetings?.length ? <EmptyState title="Sin reuniones" description="Crea la primera reunión o serie recurrente." testid="meetings-empty" /> : (
        <Table data-testid="meetings-table">
          <TableHeader><TableRow>
            <TableHead>Reunión</TableHead><TableHead>Tipo</TableHead><TableHead>Fecha</TableHead><TableHead>Estado</TableHead>
            <TableHead>Agenda</TableHead><TableHead>Quórum</TableHead><TableHead>Readiness</TableHead><TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {meetings.map((m) => {
              const fails = (m.readiness || []).filter((c) => c.blocking && !c.ok).length;
              return (
                <TableRow key={m.id} data-testid={`meeting-row-${m.id}`}>
                  <TableCell className="max-w-72">
                    <button className="font-medium text-blue-800 hover:underline" onClick={() => setDetailId(m.id)} data-testid={`meeting-open-${m.id}`}>{m.title}</button>
                    <p className="text-xs text-slate-400">{m.series_id ? `Serie (${m.series_seq}) · ` : ""}{m.location}</p>
                  </TableCell>
                  <TableCell className="text-xs">{MEETING_TYPES[m.meeting_type]}</TableCell>
                  <TableCell className="text-xs">{fmtDateTime(m.date)}</TableCell>
                  <TableCell><Chip map={MEETING_STATUS} value={m.status} testid={`meeting-status-${m.id}`} /></TableCell>
                  <TableCell className="text-xs">{(m.agenda_items || []).length} puntos</TableCell>
                  <TableCell className="text-xs" data-testid={`meeting-quorum-${m.id}`}>
                    {m.quorum?.quorum_met ? <ToneChip label={`${m.quorum.present}/${m.quorum.required}`} tone="success" /> : <span className="text-slate-400">{m.quorum?.present || 0}/{m.quorum?.required || 0}</span>}
                  </TableCell>
                  <TableCell data-testid={`meeting-readiness-${m.id}`}>
                    {["convocada", "en_curso", "celebrada"].includes(m.status) ? <ToneChip label="Superado" tone="success" />
                      : fails ? <ToneChip label={`${fails} pendientes`} tone="danger" /> : <ToneChip label="Listo" tone="success" />}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => setDetailId(m.id)} data-testid={`meeting-detail-${m.id}`}>Abrir</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {detail && <DetailDialog meeting={detail} users={users} onClose={() => setDetailId(null)} />}
    </div>
  );
}
